import React from 'react';
import styled, { css } from 'styled-components';
import { grey10, grey50, grey75, white } from '@zocdoc/branding/colors';

import {
    NON_INPUT_X_AFFORDANCE_DESKTOP,
    NON_INPUT_X_AFFORDANCE_MOBILE,
} from '../../picker-constants';

import { breakpoint } from '../../grid';
import { FontFamily } from '../../typography';

import SearchDropdownDoctor from './searchDropdownDoctor';
import SearchDropdownItem from './searchDropdownItem';

export default class SearchDropdown extends React.PureComponent {
    renderSection(header, list, listRenderer, hideSectionHeader) {
        if (list.length === 0) {
            return;
        }

        if (hideSectionHeader) {
            return (
                <div>
                    {listRenderer()}
                </div>
            );
        }

        return (
            <div>
                <SectionHeader>
                    <SectionHeaderTitle>
                        {header}
                    </SectionHeaderTitle>
                </SectionHeader>
                <SectionList>
                    {listRenderer()}
                </SectionList>
            </div>
        );
    }

    renderSectionSeparator() {
        return (
            <SectionSeparatorContainer responsive />
        );
    }

    renderSectionHeader(sectionName) {
        return (
            <SectionHeader responsive>
                {sectionName}
            </SectionHeader>
        );
    }

    renderFirstSection = () => {
        const { firstSection } = this.props;
        return (
            firstSection.map((specialty, index) =>
                (
                    <SearchDropdownItem
                        key={`${specialty.specialtyId}_${specialty.procedureId}_${specialty.name}`}
                        dataIndex={index}
                        dataTest={`section-1-option-${index}`}
                        highlight={specialty.highlight}
                        id={specialty.specialtyId}
                        name={specialty.name}
                        onClick={this.props.clickFirstSectionItemHandler}
                        onMouseMove={this.props.mouseMoveHandler}
                        onTouchStart={this.props.touchStartHandler}
                        selected={index === this.props.focusIndex}
                    />
                ))
        );
    }

    renderSecondSection = () => {
        const previousListCount = this.props.firstSection.length;

        const { secondSection } = this.props;
        return (
            secondSection.map((procedure, index) =>
                (
                    <SearchDropdownItem
                        key={`${procedure.specialtyId}_${procedure.procedureId}_${procedure.name}`}
                        dataIndex={previousListCount + index}
                        dataTest={`section-2-option-${index}`}
                        highlight={procedure.highlight}
                        id={procedure.procedureId}
                        name={procedure.name}
                        onClick={this.props.clickSecondSectionItemHandler}
                        onMouseMove={this.props.mouseMoveHandler}
                        onTouchStart={this.props.touchStartHandler}
                        selected={(previousListCount + index) === this.props.focusIndex}
                        specialtyId={procedure.specialtyId}
                    />
                ))
        );
    }

    renderThirdSection = () => {
        const previousListCount = this.props.firstSection.length + this.props.secondSection.length;

        const { thirdSection } = this.props;
        return (
            thirdSection.map((doctor, index) =>
                (
                    <SearchDropdownDoctor
                        key={doctor.id}
                        city={doctor.city}
                        dataIndex={previousListCount + index}
                        dataTest={`section-3-option-${index}`}
                        highlight={doctor.highlight}
                        id={doctor.id}
                        name={doctor.fullName}
                        onClick={this.props.clickThirdSectionItemHandler}
                        onMouseMove={this.props.mouseMoveHandler}
                        onTouchStart={this.props.touchStartHandler}
                        photoUrl={doctor.circlePictureUrl}
                        selected={(previousListCount + index) === this.props.focusIndex}
                        specialty={doctor.specialty}
                        state={doctor.state}
                        title={doctor.title}
                    />
                ))
        );
    }

    render() {
        const {
            isSpecialtyBrowse,
            firstSection,
            secondSection,
            thirdSection,
            onMouseLeave,
        } = this.props;

        const renderFirstSeparator = firstSection.length > 0 && (secondSection.length > 0 || thirdSection.length > 0);
        const renderSecondSeparator = secondSection.length > 0 && thirdSection.length > 0;
        const renderSemanticHeader = !isSpecialtyBrowse && secondSection.length > 0;

        return (
            <Dropdown
                data-test="pps-results"
                data-uem-id="pps-results"
                onMouseLeave={onMouseLeave}
                responsive
            >
                {isSpecialtyBrowse ? this.renderSectionHeader('popular specialties') : null}
                {this.renderSection('specialties', firstSection, this.renderFirstSection, true)}
                {renderFirstSeparator ? this.renderSectionSeparator(true) : null}
                {renderSemanticHeader ? this.renderSectionHeader('Are you looking for?') : null}
                {isSpecialtyBrowse ? this.renderSectionHeader('all specialties (a-z)') : null}
                {this.renderSection('visit reasons', secondSection, this.renderSecondSection, true)}
                {renderSecondSeparator ? this.renderSectionSeparator(true) : null}
                {this.renderSection('doctors', thirdSection, this.renderThirdSection, true)}
            </Dropdown>
        );
    }
}

const Dropdown = styled.div`
    background-color: ${white};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    box-sizing: border-box;
    left: 0;
    padding: 8px 24px;
    position: absolute;
    width: 100%;
    z-index: 99;
    --webkit-overflow-scrolling: touch;

    ${p => p.responsive && css`
        box-shadow: none;
        overflow-y: auto;
        padding: 11px 0 52px;
        height: 100%;

        ${breakpoint.small} {
            padding-bottom: 8px;
            height: auto;
            border-top: solid 1px ${grey10};
            border-bottom: solid 1px ${grey50};
            max-height: 372px;
            min-width: 532px;
        }
    `};
`;

const SectionHeader = styled.div`
    padding: 8px ${NON_INPUT_X_AFFORDANCE_DESKTOP}px 0 0;
    position: absolute;

    ${p => p.responsive && css`
        color: ${grey75};
        font: 15px/24px ${FontFamily.SharpSansSemibold};
        padding: 1px ${NON_INPUT_X_AFFORDANCE_MOBILE}px 4px;
        position: static;
        text-size-adjust: none;

        ${breakpoint.small} {
            padding-left: ${NON_INPUT_X_AFFORDANCE_DESKTOP}px;
            padding-right: ${NON_INPUT_X_AFFORDANCE_DESKTOP}px;
        }
    `}
`;

const SectionHeaderTitle = styled.div`
    color: ${grey75};
    padding-top: 1px;
    padding-bottom: 3px;
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    text-size-adjust: none;
    width: 100px;
`;

const SectionList = styled.div`
    padding-left: 124px;
`;

const SectionSeparatorContainer = styled.div`
    padding: ${p => p.responsive ? 6 : 8}px 0;
`;
